<template>
  <div class="loading-overlay" v-if="loading">
    <div class="loading-card">
      <div class="loading-header">
        <h3>Veriler Yükleniyor</h3>
      </div>

      <div class="circle-progress-container">
        <div
          v-for="(state, key, index) in loadingStates"
          :key="key"
          class="progress-circle"
          :class="{ completed: state }"
          :title="getItemLabel(key)"
        >
          <div class="circle-content">
            <div
              class="circle-fill"
              :style="
                state ? { transform: 'scale(1)' } : { transform: 'scale(0)' }
              "
            ></div>
            <span class="circle-index">{{ index + 1 }}</span>
          </div>
          <div class="circle-label">{{ getShortLabel(key) }}</div>
        </div>
      </div>

      <div class="loading-items">
        <div
          class="loading-item"
          :class="{ 'loading-complete': loadingStates[currentLoading] }"
        >
          <div class="loading-icon">
            <svg
              v-if="loadingStates[currentLoading]"
              class="check-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <div v-else class="pulse-loader"></div>
          </div>
          <div class="loading-text">
            <span class="loading-label">{{
              getItemLabel(currentLoading)
            }}</span>
            <span class="loading-status">{{
              loadingStates[currentLoading] ? "Tamamlandı" : "Yükleniyor..."
            }}</span>
          </div>

          <!-- İlerleme çubuğu (yükleme tamamlanmamışsa göster) -->
          <div class="item-progress" v-if="!loadingStates[currentLoading]">
            <div
              class="item-progress-fill"
              :style="{ transform: `translateX(${progressPercentage - 100}%)` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    loadingStates: {
      type: Object,
      default: () => ({
        bilanco: false,
        gelirTablosu: false,
        sermayeArtirimlari: false,
        tarihselFiyat: false,
        sirketBilgileri: false,
      }),
    },
    // Yeni özellik: Her bir adımın ilerleme yüzdesini izlemek için
    itemProgress: {
      type: Object,
      default: () => ({
        bilanco: 0,
        gelirTablosu: 0,
        sermayeArtirimlari: 0,
        tarihselFiyat: 0,
        sirketBilgileri: 0,
      }),
    },
  },
  data() {
    return {
      labels: {
        bilanco: "Bilanço Verileri",
        gelirTablosu: "Gelir Tablosu",
        sermayeArtirimlari: "Sermaye Artırımları ve Temettüler",
        tarihselFiyat: "Tarihsel Fiyat Bilgileri",
        sirketBilgileri: "Şirket Bilgileri",
      },
      shortLabels: {
        bilanco: "Bilanço",
        gelirTablosu: "Gelir",
        sermayeArtirimlari: "Sermaye",
        tarihselFiyat: "Fiyat",
        sirketBilgileri: "Şirket",
      },
    };
  },
  computed: {
    progressPercentage() {
      const totalItems = Object.keys(this.loadingStates).length;
      const completedItems = Object.values(this.loadingStates).filter(
        (state) => state
      ).length;

      // Tamamlanmış öğelerin yüzdesi
      const completedPercentage = (completedItems / totalItems) * 100;

      // Mevcut yüklenen öğenin kısmi ilerlemesi
      let currentItemProgress = 0;
      if (this.currentLoading && !this.loadingStates[this.currentLoading]) {
        currentItemProgress = this.itemProgress[this.currentLoading] || 0;
      }

      // Her tamamlanmış öğe toplam ilerlemenin bir kısmını temsil eder
      const singleItemWeight = 100 / totalItems;

      // Toplam ilerleme: tamamlanmış öğeler + şu anki öğenin kısmi ilerlemesi
      const total =
        completedPercentage + (currentItemProgress * singleItemWeight) / 100;

      return Math.round(total);
    },
    currentLoading() {
      // Return the key of the first item that is not completed
      for (const [key, value] of Object.entries(this.loadingStates)) {
        if (!value) return key;
      }
      // If all items are completed, return the last key
      const keys = Object.keys(this.loadingStates);
      return keys[keys.length - 1];
    },
    // Mevcut yüklenen öğe için ilerleme yüzdesi
    currentItemProgressPercentage() {
      return this.itemProgress[this.currentLoading] || 0;
    },
  },
  methods: {
    getItemLabel(key) {
      return this.labels[key] || key;
    },
    getShortLabel(key) {
      return this.shortLabels[key] || key;
    },
    // Bileşen dışından ilerleme yüzdesini güncellemek için yardımcı metot
    updateItemProgress(key, percentage) {
      if (key && Object.prototype.hasOwnProperty.call(this.itemProgress, key)) {
        this.$set(this.itemProgress, key, percentage);
      }
    },
  },
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(4px);
}

.loading-card {
  background-color: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border: 1px solid var(--border-color);
}

.loading-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
}

.loading-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 24px;
  display: flex;
  align-items: center;
}

.circle-progress-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 0.5rem 0;
  padding: 0.5rem;
}

.progress-circle {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.circle-content {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.circle-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--primary-light);
  border-radius: 50%;
  transform: scale(0);
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.circle-index {
  position: relative;
  color: var(--text-primary);
  font-weight: 600;
  z-index: 2;
  transition: color 0.3s;
}

.completed .circle-index {
  color: white;
}

.circle-label {
  font-size: 12px;
  color: var(--text-secondary);
  text-align: center;
  white-space: nowrap;
}

.loading-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: var(--card-dark);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.loading-complete {
  background-color: rgba(16, 185, 129, 0.1);
  border-left: 3px solid var(--success-color);
}

.loading-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.check-icon {
  color: var(--success-color);
  width: 24px;
  height: 24px;
  animation: scaleUp 0.3s ease;
}

.pulse-loader {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--primary-light);
  animation: pulse 1.5s infinite;
}

.loading-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  z-index: 2;
}

.loading-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.loading-status {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

/* Gerçek ilerlemeye dayalı ilerleme çubuğu */
.item-progress {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}

.item-progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--primary-light);
  opacity: 0.12;
  transform: translateX(-100%);
  transition: transform 0.3s ease-out;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
}

@keyframes scaleUp {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 480px) {
  .loading-card {
    padding: 1rem;
    gap: 1rem;
  }

  .loading-item {
    padding: 0.5rem;
  }

  .loading-label {
    font-size: 13px;
  }

  .loading-status {
    font-size: 11px;
  }

  .circle-content {
    width: 32px;
    height: 32px;
  }

  .circle-label {
    font-size: 10px;
  }
}
</style>
